import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Button,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAppDispatch } from "../../../app/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { loadAllDocumentProcesses } from "../documentsApi";
import { ProjectProcessHeader } from "../../project/projectProcess/ProjectProcessApiClient";
import { useNavigate } from "react-router-dom";
import { Localized } from "@fluent/react";
import { ValidationProcessStatusPill } from "../../project/validationProcess/components/ValidationProcessStatusPill";
import { getProcessLink } from "../../project/helpers/ProcessLinkProvider";

export const DocumentAllProcessesPanel = ({ documentId }: { documentId: string }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [data, setData] = useState<ProjectProcessHeader[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState<any>(null);

    const load = useCallback(() => {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        return dispatch(loadAllDocumentProcesses(documentId))
            .then(unwrapResult)
            .then(setData)
            .catch((e) => {
                console.error(e);
                setIsError(true);
                setError(e);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch, documentId]);

    useEffect(() => {
        load();
    }, [load]);

    const parseDate = (v: any): Date | null => {
        if (!v) return null;
        const d = v instanceof Date ? v : new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const formatDate = (v: any) => {
        const d = parseDate(v);
        if (!d) return "-";
        return d.toLocaleString("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" });
    };

    const sorted = useMemo(() => {
        const arr = data ?? [];
        return [...arr].sort((a, b) => {
            const aKey = parseDate(a.startDate)?.getTime() ?? parseDate(a.createDate)?.getTime() ?? 0;
            const bKey = parseDate(b.startDate)?.getTime() ?? parseDate(b.createDate)?.getTime() ?? 0;
            return bKey - aKey;
        });
    }, [data]);


    if (isLoading)
        return <LoadingContainer />;
    if (isError)
        return <ErrorContainer error={error} />;

    return (
        <Paper elevation={1} sx={{ mt: 1, mb: 1, overflow: "hidden" }}>
            <Box
                sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    <Localized id="document-processes">_Procesy dokumentu</Localized>
                </Typography>
            </Box>

            <Divider />

            <TableContainer>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Localized id="process-no">_Nr</Localized>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Localized id="process-name">_Nazwa</Localized>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Localized id="status">_Status</Localized>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Localized id="process-date-from-short">_Start</Localized>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <Localized id="process-date-to-short">_Koniec</Localized>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sorted.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" sx={{ py: 2, fontStyle: "italic" }} color="text.secondary">
                                        <Localized id="document-processes-none">
                                            _Brak procesów dla tego dokumentu.
                                        </Localized>
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sorted.map((p) => {
                                return (
                                    <TableRow
                                        key={String(p.id)}
                                        hover
                                    >
                                        <TableCell>{p.no || "-"}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {p.name || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <ValidationProcessStatusPill status={p.status ?? ""} size="small" dense />
                                        </TableCell>
                                        <TableCell>{formatDate(p.startDate)}</TableCell>
                                        <TableCell>{formatDate(p.finishDate)}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="text"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                                onClick={() => navigate(getProcessLink(p.processType, p.id))}
                                            >
                                                <Localized id="open">_open</Localized>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};